import type { ActorEvent, ListViewConfig } from '@lighthouse/core'
import {
    type RecordLikeProtocol,
    type RichTextContentProtocol,
    type TableColumns,
    VIEW_DESIGN_STYLE,
    type ViewField
} from '@lighthouse/core'
import { getViewColumns, ViewEmptyState } from '@lighthouse/shared'
import { find } from 'rambda'
import React, { memo, useCallback, useMemo } from 'react'

import { useEffectiveAnchorId, useViewBlockContext } from '../../context'
import { useScrollToAnchorWhenHashMatch } from '../../hooks/useScrollToAnchorWhenHashMatch'
import { List } from './List'
import * as SC from './styles'
import { getDesignTypeMap } from './utils'

interface ListBlockProps {
    tablePropsCache?: TableColumns
    selectedRecords: string[]
    blockWidth?: number
    onSelectedRecords: (recordIds: string[]) => void
    onRecordOperatorActionTrigger?: (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) => Promise<void>
    onRecordClick?: (id: string, actorEvent: ActorEvent, record: RecordLikeProtocol) => Promise<void>
    onRenderRichText: (v: RichTextContentProtocol) => string
}

/**
 *
 * @param param
 * @returns
 * blockData: {
 *   fields: { id: string; type: string; name: string }[]
 *   records: { id: string; content: Record<number, string> }[]
 * }
 */

const ListBlock: React.FC<ListBlockProps> = ({
    tablePropsCache,
    selectedRecords,
    blockWidth,
    onSelectedRecords,
    onRecordOperatorActionTrigger,
    onRecordClick,
    onRenderRichText
}) => {
    const { dataSource, blockData, recordPoints } = useViewBlockContext()
    const { schema, id: dsId, viewOptions } = dataSource
    const { tableProps } = viewOptions
    const { id, title = '无标题', config } = blockData
    const {
        anchor,
        pointer,
        canDelete,
        canExport,
        canPrint,
        canDisplay,
        viewFieldSettings,
        highLightRules,
        designType,
        designStyle = VIEW_DESIGN_STYLE.simple,
        listAvatar,
        isShowListAvatar,
        avatarType,
        isShowEmphasizeText,
        emphasizeTextField,
        recordOperator,
        breakPoint: baseBreakPointConfig,
        eventList
    } = config as ListViewConfig
    const { emptyView } = baseBreakPointConfig || {}
    const { anchorId } = anchor || {}
    const effectiveAnchorId = useEffectiveAnchorId(anchorId)
    useScrollToAnchorWhenHashMatch(effectiveAnchorId, anchor)
    const listItemCheckable = !!canDelete || !!canExport || !!canPrint
    // const [selectedRecords, setSelectedRecords] = useState<string[]>([])
    // const [selectedMode, setSelectedMode] = useState<SelectedMode>(undefined)
    const noData = !pointer

    const designConfig = useMemo(
        () => ({
            designStyle,
            listAvatar,
            isShowListAvatar,
            avatarType,
            isShowEmphasizeText,
            emphasizeTextField,
            designType
        }),
        [avatarType, designStyle, designType, emphasizeTextField, isShowEmphasizeText, isShowListAvatar, listAvatar]
    )

    const designTypeOption = useMemo(() => getDesignTypeMap(designType), [designType])

    const settingColumns = useMemo(
        () =>
            getViewColumns({
                blockId: id,
                tableProps,
                value: viewFieldSettings,
                schema
            }),
        [id, schema, tableProps, viewFieldSettings]
    )

    // 用户字段设置后的tableProps
    const [columns, userColumns] = useMemo(() => {
        if (!tablePropsCache || tablePropsCache.length === 0 || !canDisplay) {
            const list = settingColumns.filter(item => {
                return item.visible
            })
            return [list, list]
        }

        const columnsCache = tablePropsCache.reduce<ViewField[]>((prev, cur) => {
            const field = find(({ fieldId }) => fieldId === cur.id, settingColumns)
            if (field) {
                prev.push({
                    ...field,
                    visible: cur.visible
                })
            }
            return prev
        }, [])
        const columns = settingColumns.reduce<ViewField[]>((prev, cur) => {
            if (!cur.visible) {
                return prev
            }
            const field = find(({ fieldId }) => fieldId === cur.fieldId, prev)
            if (!field) {
                prev.push({
                    ...cur,
                    visible: false
                })
            }
            return prev
        }, columnsCache)

        const userColumns = columns.filter(item => item.visible)
        return [columns, userColumns]
    }, [tablePropsCache, canDisplay, settingColumns])

    const primaryField = useMemo(() => {
        const viewFieldId = find(item => item.visible, userColumns)?.fieldId
        if (!viewFieldId) {
            return
        }
        return schema[viewFieldId]
    }, [schema, userColumns])

    const handleSelect = useCallback(
        (recordIds: string[]) => {
            onSelectedRecords(recordIds)
        },
        [onSelectedRecords]
    )

    return useMemo(() => {
        if (noData || !dataSource || recordPoints?.length === 0) {
            return (
                <SC.TableNoDataContent id={effectiveAnchorId}>
                    <ViewEmptyState config={emptyView} onRenderRichText={onRenderRichText} />
                </SC.TableNoDataContent>
            )
        }

        return (
            <SC.ListScrollerContent border={designTypeOption.border} id={effectiveAnchorId} data-content-type="List">
                <List
                    eventList={eventList}
                    checkable={listItemCheckable}
                    primaryField={primaryField}
                    viewFields={userColumns}
                    selectedRecords={selectedRecords}
                    designConfig={designConfig}
                    onRecordSelect={handleSelect}
                    highLightRules={highLightRules}
                    recordOperator={recordOperator}
                    onRecordOperatorActionTrigger={onRecordOperatorActionTrigger}
                    onRecordClick={onRecordClick}
                    onRenderButtonTitle={onRenderRichText}
                />
            </SC.ListScrollerContent>
        )
    }, [
        noData,
        dataSource,
        recordPoints?.length,
        designTypeOption.border,
        effectiveAnchorId,
        eventList,
        listItemCheckable,
        primaryField,
        userColumns,
        selectedRecords,
        designConfig,
        handleSelect,
        highLightRules,
        recordOperator,
        onRecordOperatorActionTrigger,
        onRecordClick,
        onRenderRichText,
        emptyView
    ])
}

export default memo(ListBlock)
